import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import { MY_PATH_URL } from '../../paths/utils/pathsChatUrl';
import { caseStudyAPI } from '../../case-study/api/casestudyAPI';
import { addBubbleToStorage } from '../context/AppStoreHomeContext';
import CaseStudyWrapper from './CaseStudyWrapper';
import WindmillGame from './WindmillGame';
import AppleGame from './AppleGame';
import SputnikGame from './SputnikGame';
import WebsiteInterviewPage from '../../website-interview/components/WebsiteInterviewPage';
import WebsiteReasoningPage from '../../website-reasoning/components/WebsiteReasoningPage';
import UserProfileDropdown from '../../../components/UserProfileDropdown/UserProfileDropdown';
import { Logo_black } from '../../../components/Logo/Logo';
import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary';
import { resolveAppLaunchTarget, navigateAppLaunch, isStandaloneReasoningApp } from '../utils/appDisplayUtils';
import './AppExperience.css';

// Kept for any listeners; footer itself is now path-gated to /chat|/apps|/cases only.
export const CASE_STUDY_ACTIVE_EVENT = 'ivori:case-study-active-change';

const AppExperience = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [app, setApp] = useState(null);
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('case-study'); // 'case-study', 'interview', 'tests', 'complete'
  const [caseStudyCompleted, setCaseStudyCompleted] = useState(false);
  const [interviewCompleted, setInterviewCompleted] = useState(false);
  const [testsCompleted, setTestsCompleted] = useState(false);
  const [gameCompleted, setGameCompleted] = useState(false);
  const appOpenedRecorded = useRef(false);
  const sectionStartedAt = useRef({});
  const sectionStartedRecorded = useRef({});

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(CASE_STUDY_ACTIVE_EVENT, { detail: { active: currentStep === 'case-study' } })
    );
    return () => {
      window.dispatchEvent(new CustomEvent(CASE_STUDY_ACTIVE_EVENT, { detail: { active: false } }));
    };
  }, [currentStep]);

  const userName = user?.name || 'Student';
  const userEmail = user?.email || 'student@pathbuddy.com';

  // Fetch app and case study data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const appData = await appsAPI.getAppById(id);

        // Redirect if app is not owned and not unlocked (detail page can purchase/add)
        if (appData && appData.is_owned === false && appData.is_unlocked !== true) {
          navigate(`/apps-store/${id}`, { replace: true });
          return;
        }

        setApp(appData);

        // Standalone reasoning apps must use /website-reasoning-test?type= — never this suite shell
        if (isStandaloneReasoningApp(appData)) {
          const target = resolveAppLaunchTarget(appData);
          if (target) {
            navigateAppLaunch(navigate, target, { replace: true });
            return;
          }
        }

        const checkExamName = (appData?.exam_name || '').trim().toLowerCase();
        const checkAppTitle = (appData?.title || '').trim().toLowerCase();
        if (checkExamName === 'caius_explore' || checkAppTitle === 'caius explore') {
          navigate(`/apps-store/${id}/caius-explore`, { replace: true });
          return;
        }

        // Tool apps: no case study – go straight to the right screen
        if (appData?.app_type === 'productivity' || appData?.app_type === 'utility') {
          const toolLaunch = resolveAppLaunchTarget(appData);
          if (toolLaunch) {
            navigateAppLaunch(navigate, toolLaunch);
            return;
          }
          const examName = appData.exam_name || 'cv';
          if (examName === 'my_path') {
            navigate(MY_PATH_URL, { replace: true });
          } else if (examName === 'financial_modeling_lab') {
            navigate('/financial-modeling-lab', { replace: true });
          } else if (examName === 'flash_cards' || (appData.title || '').trim().toLowerCase() === 'flash cards') {
            navigate(`/apps-store/${id}/flash-cards`, { replace: true });
          } else if (examName === 'flowforge' || (appData.title || '').trim().toLowerCase() === 'flowforge') {
            navigate(`/apps-store/${id}/flowforge`, { replace: true });
          } else if (
            examName === 'custom_quiz_generator' ||
            (appData.title || '').trim().toLowerCase().includes('custom quiz')
          ) {
            navigate('/custom-quiz-generator', { replace: true });
          } else if (examName === 'notes' || (appData.title || '').trim().toLowerCase() === 'notes') {
            navigate('/notes', { replace: true });
          } else if (examName === 'library' || (appData.title || '').trim().toLowerCase() === 'library' || (appData.title || '').trim().toLowerCase() === 'smart library') {
            navigate(`/apps-store/${id}/library`, { replace: true });
          } else if (
            examName === 'supercurricular_books' ||
            (appData.title || '').trim().toLowerCase() === 'supercurricular books'
          ) {
            navigate(`/apps-store/${id}/supercurricular-books`, { replace: true });
          } else {
            navigate(`/application-builder/form/${examName}`, { replace: true });
          }
          return;
        }

        // If this is a standalone game app (direct link), redirect to the game route.
        const appTitleNorm = (appData.title || '').trim().toLowerCase();
        const isWindmillApp = appData.creator === 'Ash.exe' && appData.subject === 'Engineering' && appTitleNorm === 'windmill';
        const isAppleGameApp = appData.creator === 'Ash.exe' && appData.subject === 'Finance' && appTitleNorm === 'the legacy test';
        const isSputnikGameApp = appData.creator === 'Ash.exe' && appData.subject === 'Engineering' && (appTitleNorm === 'into the void' || appTitleNorm === 'sputnik');
        const isGameStudioApp = appTitleNorm === 'game studio';
        if (isWindmillApp) {
          navigate(`/apps-store/${id}/windmill-game`, { replace: true });
          return;
        }
        if (isAppleGameApp) {
          navigate(`/apps-store/${id}/apple-game`, { replace: true });
          return;
        }
        if (isSputnikGameApp) {
          navigate(`/apps-store/${id}/sputnik-game`, { replace: true });
          return;
        }
        if (isGameStudioApp) {
          navigate(`/apps-store/${id}/game-studio`, { replace: true });
          return;
        }

        if (appData && appData.case_study_id) {
          try {
            const caseStudyData = await caseStudyAPI.getCaseStudyById(appData.case_study_id);
            if (caseStudyData) {
              // Also fetch figures/exhibits for the interview context
              try {
                const figuresData = await caseStudyAPI.getCaseStudyFigures(appData.case_study_id);
                if (figuresData && figuresData.length > 0) {
                  caseStudyData.figures = figuresData;
                }
              } catch (figuresError) {
                console.warn('Could not fetch figures:', figuresError);
                // Continue without figures
              }
              setCaseStudy(caseStudyData);
            } else {
              console.warn('Case study data is null or undefined');
            }
          } catch (caseStudyError) {
            console.error('Error fetching case study:', caseStudyError);
            // Don't fail the whole experience if case study fails
            // Just log it and continue
          }
        } else {
          console.warn('App does not have a case_study_id');
        }
        // Reasoning-only apps (no case study, no interview): start at tests step
        const examLower = (appData.exam_name || '').trim().toLowerCase();
        const examTypeMap = {
          'logical reasoning': 'logical',
          logical_reasoning: 'logical',
          logical: 'logical',
          'pattern recognition': 'pattern',
          pattern_recognition: 'pattern',
          pattern: 'pattern',
          'numerical reasoning': 'numerical',
          numerical_reasoning: 'numerical',
          numerical: 'numerical',
          'verbal reasoning': 'verbal',
          verbal_reasoning: 'verbal',
          verbal: 'verbal',
        };
        const reasoningOnly = !appData.case_study_id && !appData.has_video_interview && (
          !!examTypeMap[examLower] ||
          appData.has_logical_reasoning || appData.has_pattern_recognition ||
          appData.has_numerical_reasoning || appData.has_verbal_reasoning
        );
        if (reasoningOnly) {
          setCurrentStep('tests');
        }
      } catch (error) {
        console.error('Error fetching app data:', error);
        // Set error state so we can show a proper error message
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Record usage to DB (fire-and-forget, no blocking UI)
  const recordUsage = (activityType, options = {}) => {
    if (!id || !app) return;
    appsAPI.recordUsage(id, activityType, options).catch((err) => {
      console.warn('Usage tracking failed:', err);
    });
  };

  const getSectionDurationSeconds = (section) => {
    const t = sectionStartedAt.current[section];
    return t != null ? Math.round((Date.now() - t) / 1000) : 0;
  };

  // Record app_opened once when user enters the app experience
  useEffect(() => {
    if (app && id && !appOpenedRecorded.current) {
      appOpenedRecorded.current = true;
      appsAPI.recordUsage(id, 'app_opened').catch((err) => console.warn('Usage tracking failed:', err));
    }
  }, [app, id]);

  // Record section started once when user enters a step (for duration tracking)
  useEffect(() => {
    if (!app || !id) return;
    const step = currentStep;
    if (step === 'case-study' && !sectionStartedRecorded.current['case-study']) {
      sectionStartedRecorded.current['case-study'] = true;
      sectionStartedAt.current['case-study'] = Date.now();
      recordUsage('case_study_started', { metadata: { section: 'case_study' } });
    } else if (step === 'interview' && !sectionStartedRecorded.current['interview']) {
      sectionStartedRecorded.current['interview'] = true;
      sectionStartedAt.current['interview'] = Date.now();
      recordUsage('video_interview_started', { metadata: { section: 'video_interview' } });
    } else if (step === 'tests' && !sectionStartedRecorded.current['tests']) {
      sectionStartedRecorded.current['tests'] = true;
      sectionStartedAt.current['tests'] = Date.now();
      recordUsage('reasoning_test_started', { metadata: { section: 'reasoning_tests' } });
    } else if (step === 'game' && !sectionStartedRecorded.current['game']) {
      sectionStartedRecorded.current['game'] = true;
      sectionStartedAt.current['game'] = Date.now();
      recordUsage('game_started', { metadata: { section: 'game', attached_app_id: app?.attached_game_app_id } });
    }
  }, [currentStep, app, id]);

  // Handle case study completion (user clicked "Continue to Next Step")
  const handleCaseStudyComplete = () => {
    setCaseStudyCompleted(true);
    const duration_seconds = getSectionDurationSeconds('case-study');
    recordUsage('case_study_completed', {
      referenceType: 'case_study',
      referenceId: app?.case_study_id,
      metadata: { duration_seconds, completion_percentage: 100, section: 'case_study' },
    });
    if (app?.has_video_interview) {
      setCurrentStep('interview');
    } else if (hasReasoningTests()) {
      setCurrentStep('tests');
    } else if (app?.attached_game_app_id) {
      setCurrentStep('game');
    } else {
      recordUsage('experience_completed', { metadata: { duration_seconds: 0 } });
      setCurrentStep('complete');
    }
  };

  // Handle case study skip (user clicked "Skip to Next Step")
  const handleCaseStudySkip = () => {
    setCaseStudyCompleted(true);
    const duration_seconds = getSectionDurationSeconds('case-study');
    recordUsage('case_study_skipped', {
      metadata: { duration_seconds, skipped: true, section: 'case_study', completion_percentage: 0 },
    });
    if (app?.has_video_interview) {
      setCurrentStep('interview');
    } else if (hasReasoningTests()) {
      setCurrentStep('tests');
    } else if (app?.attached_game_app_id) {
      setCurrentStep('game');
    } else {
      recordUsage('experience_completed', { metadata: { duration_seconds: 0 } });
      setCurrentStep('complete');
    }
  };

  // Handle interview completion (user finished interview)
  const handleInterviewComplete = (completionMetadata = {}) => {
    setInterviewCompleted(true);
    const duration_seconds = getSectionDurationSeconds('interview');
    recordUsage('video_interview_completed', {
      metadata: {
        duration_seconds,
        completion_percentage: completionMetadata.completion_percentage ?? 100,
        section: 'video_interview',
        ...completionMetadata,
      },
    });
    if (hasReasoningTests()) {
      setCurrentStep('tests');
    } else if (app?.attached_game_app_id) {
      setCurrentStep('game');
    } else {
      recordUsage('experience_completed', { metadata: { duration_seconds: 0 } });
      setCurrentStep('complete');
    }
  };

  // Handle interview skip (user clicked "Skip Interview")
  const handleInterviewSkip = () => {
    setInterviewCompleted(true);
    const duration_seconds = getSectionDurationSeconds('interview');
    recordUsage('video_interview_skipped', {
      metadata: { duration_seconds, skipped: true, section: 'video_interview', completion_percentage: 0 },
    });
    if (hasReasoningTests()) {
      setCurrentStep('tests');
    } else if (app?.attached_game_app_id) {
      setCurrentStep('game');
    } else {
      recordUsage('experience_completed', { metadata: { duration_seconds: 0 } });
      setCurrentStep('complete');
    }
  };

  // Handle tests completion (user finished reasoning tests)
  const handleTestsComplete = (completionMetadata = {}) => {
    setTestsCompleted(true);
    const duration_seconds = getSectionDurationSeconds('tests');
    recordUsage('reasoning_test_completed', {
      metadata: {
        duration_seconds,
        completion_percentage: completionMetadata.completion_percentage ?? 100,
        section: 'reasoning_tests',
        ...completionMetadata,
      },
    });
    if (app?.attached_game_app_id) {
      setCurrentStep('game');
    } else {
      recordUsage('experience_completed', { metadata: { duration_seconds: 0 } });
      setCurrentStep('complete');
    }
  };

  // Handle tests skip (user clicked "Skip Tests")
  const handleTestsSkip = () => {
    setTestsCompleted(true);
    const duration_seconds = getSectionDurationSeconds('tests');
    recordUsage('reasoning_test_skipped', {
      metadata: { duration_seconds, skipped: true, section: 'reasoning_tests', completion_percentage: 0 },
    });
    if (app?.attached_game_app_id) {
      setCurrentStep('game');
    } else {
      recordUsage('experience_completed', { metadata: { duration_seconds: 0 } });
      setCurrentStep('complete');
    }
  };

  // Handle game completion (attached game app finished as final step of case)
  const handleGameComplete = () => {
    setGameCompleted(true);
    const duration_seconds = getSectionDurationSeconds('game');
    recordUsage('experience_completed', { metadata: { duration_seconds, section: 'game' } });
    setCurrentStep('complete');
  };

  // Handle game skip (user clicked "Skip Game")
  const handleGameSkip = () => {
    const duration_seconds = getSectionDurationSeconds('game');
    recordUsage('game_skipped', { metadata: { duration_seconds, section: 'game', skipped: true } });
    setCurrentStep('complete');
  };

  // Map standalone reasoning exam names → WebsiteReasoningPage type keys
  const examNameToAssessmentType = (examName) => {
    const map = {
      'logical reasoning': 'logical',
      'logical_reasoning': 'logical',
      logical: 'logical',
      'pattern recognition': 'pattern',
      'pattern_recognition': 'pattern',
      pattern: 'pattern',
      'numerical reasoning': 'numerical',
      'numerical_reasoning': 'numerical',
      numerical: 'numerical',
      'verbal reasoning': 'verbal',
      'verbal_reasoning': 'verbal',
      verbal: 'verbal',
    };
    return map[(examName || '').trim().toLowerCase()] || null;
  };

  // Check if app has reasoning tests
  const hasReasoningTests = () => {
    if (!app) return false;
    if (examNameToAssessmentType(app.exam_name)) return true;
    return app.has_logical_reasoning ||
           app.has_pattern_recognition ||
           app.has_numerical_reasoning ||
           app.has_verbal_reasoning;
  };

  // Get assessment types for tests — prefer exam_name so standalone apps stay single-type
  const getAssessmentTypes = () => {
    if (!app) return [];
    const fromExam = examNameToAssessmentType(app.exam_name);
    if (fromExam && !app.case_study_id) return [fromExam];
    const types = [];
    if (app.has_logical_reasoning) types.push('logical');
    if (app.has_pattern_recognition) types.push('pattern');
    if (app.has_numerical_reasoning) types.push('numerical');
    if (app.has_verbal_reasoning) types.push('verbal');
    if (types.length === 0 && fromExam) types.push(fromExam);
    return types;
  };

  // Skip to next step (user clicked Skip button – record skipped, then advance)
  const handleSkip = () => {
    if (currentStep === 'case-study') {
      handleCaseStudySkip();
    } else if (currentStep === 'interview') {
      handleInterviewSkip();
    } else if (currentStep === 'tests') {
      handleTestsSkip();
    } else if (currentStep === 'game') {
      handleGameSkip();
    }
  };

  if (loading) {
    return (
      <div className="app-experience-loading">
        <div className="loading-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="app-experience netflix-style">
        <div className="app-experience-error">
          <p>App not found</p>
          <button onClick={() => navigate('/apps')} className="btn-back-to-store">
            Back to Apps
          </button>
        </div>
      </div>
    );
  }

  // Reasoning-only: no case study, no interview, just tests
  const reasoningOnly = !app?.case_study_id && !app?.has_video_interview && hasReasoningTests();
  const steps = reasoningOnly
    ? [{ id: 'tests', label: 'Reasoning Tests', completed: testsCompleted }]
    : [
        { id: 'case-study', label: 'Case Study', completed: caseStudyCompleted },
        ...(app?.has_video_interview ? [{ id: 'interview', label: 'Interview', completed: interviewCompleted }] : []),
        ...(hasReasoningTests() ? [{ id: 'tests', label: 'Tests', completed: testsCompleted }] : []),
        ...(app?.attached_game_app_id ? [{ id: 'game', label: 'Game', completed: gameCompleted }] : [])
      ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const attachedGameTitle = (app?.attached_game?.title || '').toLowerCase();
  const attachedGameType = attachedGameTitle.includes('legacy') || attachedGameTitle.includes('apple')
    ? 'apple'
    : (attachedGameTitle.includes('sputnik') || attachedGameTitle.includes('void') ? 'sputnik' : 'windmill');

  return (
    <div className="app-experience netflix-style">
      {/* Header — hidden on case-study step (CaseStudy has its own floating back) */}
      {currentStep !== 'case-study' && (
      <header className="app-experience-header">
        <div className="app-experience-header-content">
          <div className="app-experience-left-section">
            <div className="app-experience-logo">
              <Logo_black onClick={() => navigate('/apps')} />
            </div>
            <h1 className="app-experience-title">{app.title}</h1>
          </div>
          
          <div className="app-experience-header-actions">
            <button
              type="button"
              className="app-experience-pause-btn"
              onClick={() => {
                const duration = Object.keys(sectionStartedAt.current).reduce((acc, k) => acc + (getSectionDurationSeconds(k) || 0), 0);
                const stepLabel = steps.find((s) => s.id === currentStep)?.label || currentStep;
                recordUsage('experience_paused', {
                  metadata: { section: currentStep, duration_seconds: duration, paused_at: new Date().toISOString() },
                });
                addBubbleToStorage({
                  type: 'paused_app',
                  title: `${app.title} – ${stepLabel}`,
                  subtitle: 'Paused – click to resume',
                  link: `/apps-store/${id}/experience`,
                  data: { appId: id, currentStep, stepLabel, duration_seconds: duration },
                });
                navigate('/apps');
              }}
            >
              Pause / Back to apps
            </button>
          </div>
        </div>
      </header>
      )}

      {/* Progress Bar (Hidden for now, will fix better later) */}
      {false && steps.length > 1 && (
        <div className="app-experience-progress">
          <div className="app-experience-progress-bar">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className={`app-experience-progress-step ${step.completed ? 'completed' : ''} ${step.id === currentStep ? 'active' : ''}`}>
                  <div className="app-experience-progress-step-circle">
                    {step.completed ? '✓' : index + 1}
                  </div>
                  <span className="app-experience-progress-step-label">{step.label}</span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`app-experience-progress-line ${step.completed ? 'completed' : ''}`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Step Content */}
      <main className="app-experience-main">
        {currentStep === 'case-study' && (
          <div className="app-experience-step">
            <div className="app-experience-step-header">
              <h2>Case Study</h2>
              <button onClick={handleSkip} className="app-experience-skip-btn">
                Skip to Next Step
              </button>
            </div>
            <div className="app-experience-case-study-wrapper">
              {app?.case_study_id && caseStudy ? (
                <ErrorBoundary>
                  <CaseStudyWrapper 
                    caseStudyId={app.case_study_id} 
                    caseStudy={caseStudy}
                    onComplete={handleCaseStudyComplete}
                  />
                </ErrorBoundary>
              ) : app?.case_study_id ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <p>Loading case study...</p>
                </div>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <p>Case study not available for this app. Click "Skip to Next Step" to continue.</p>
                  <button onClick={handleSkip} className="app-experience-skip-btn" style={{ marginTop: '20px' }}>
                    Skip to Next Step
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {currentStep === 'interview' && app.has_video_interview && (
          <div className="app-experience-step">
            <div className="app-experience-step-header">
              <h2>Video Interview</h2>
              <button onClick={handleSkip} className="app-experience-skip-btn">
                Skip Interview
              </button>
            </div>
            <div className="app-experience-interview-wrapper">
              <WebsiteInterviewPage 
                caseStudyContext={caseStudy}
                appContext={app}
                onComplete={handleInterviewComplete}
              />
            </div>
          </div>
        )}

        {currentStep === 'tests' && hasReasoningTests() && (
          <div className="app-experience-step">
            <div className="app-experience-step-header">
              <h2>Reasoning Tests</h2>
              <button onClick={handleTestsSkip} className="app-experience-skip-btn">
                Skip Tests
              </button>
            </div>
            <div className="app-experience-tests-wrapper">
              <ErrorBoundary>
                <WebsiteReasoningPage 
                  assessmentTypes={getAssessmentTypes()}
                  caseStudyContext={caseStudy}
                  appContext={app}
                  onComplete={handleTestsComplete}
                />
              </ErrorBoundary>
            </div>
          </div>
        )}

        {currentStep === 'game' && app?.attached_game_app_id && (
          <div className="app-experience-step app-experience-step-game">
            <div className="app-experience-step-header">
              <h2>Game</h2>
              <button onClick={handleSkip} className="app-experience-skip-btn">
                Skip Game
              </button>
            </div>
            {attachedGameType === 'apple' ? (
              <AppleGame
                appId={app.attached_game_app_id}
                parentAppId={id}
                onComplete={handleGameComplete}
              />
            ) : attachedGameType === 'sputnik' ? (
              <SputnikGame
                appId={app.attached_game_app_id}
                parentAppId={id}
                onComplete={handleGameComplete}
              />
            ) : (
              <WindmillGame
                appId={app.attached_game_app_id}
                parentAppId={id}
                onComplete={handleGameComplete}
              />
            )}
          </div>
        )}

        {currentStep === 'complete' && (
          <div className="app-experience-complete">
            <div className="app-experience-complete-content">
              <div className="app-experience-complete-icon">✓</div>
              <h2>Experience Complete!</h2>
              <p>You've completed all components of this app experience.</p>
              <div className="app-experience-complete-summary">
                {caseStudyCompleted && <div className="complete-item">✓ Case Study</div>}
                {interviewCompleted && <div className="complete-item">✓ Video Interview</div>}
                {testsCompleted && <div className="complete-item">✓ Reasoning Tests</div>}
                {gameCompleted && <div className="complete-item">✓ Game</div>}
              </div>
              <div className="app-experience-complete-actions">
                <button 
                  onClick={() => navigate(`/apps-store/${id}`)}
                  className="btn-back-to-detail"
                >
                  Back to App Details
                </button>
                <button 
                  onClick={() => navigate('/apps')}
                  className="btn-back-to-store"
                >
                  Browse More Apps
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AppExperience;
